/**
 * The local route's fallback to the tunnel (plan
 * `docs/plans/LOCAL-FIRST-CONNECT.md` §2/D2 step 3, §2/D5).
 *
 * The route is **resolved, not offered**: every connect goes through
 * `services/hubConnect.ts` → `connectResolvingRoute` (the account's own token at
 * the hub's LAN address, the tunnel only as its fallback), and
 * `services/hubLocalFirst.ts` is what claims. The offer machinery this module
 * used to carry — the availability check, the dialog's per-hub preference
 * record, the silent switch — is deleted with card `t_0de502fd`: nothing here
 * asks a question or stores a preference.
 *
 * What survives, and is all that is left, is decision 5's other half: **LAN
 * presence never disables the cloud route.** A window that IS on a hub's LAN
 * route when that route stops answering hands the session back to the tunnel and
 * says so once (`services/hubRouteNotice.ts` holds the sentence and its latch,
 * because the connect path states the same kind of sentence for its own reason).
 */
import { getActiveHubEndpoint, getActiveHubOwnId, getRuntimeConfig, readTabActiveHub } from '../config/runtime';
import { connectCloudSilently, parseHubApiUrl } from './hubConnect';
import { clearRouteFallback, routeFallback, stateRouteFallback } from './hubRouteNotice';
import { useAuthStore } from '../stores/auth';
import { useSystemStore } from '../stores/system';
import { useWebRTCStore } from '../stores/webrtc';

/** The name of the hub this window is on, for the one fallback sentence. */
function readActiveHubName(): string {
  try {
    return useSystemStore().name || '';
  } catch {
    // A store that is not ready yet leaves the hub's id as the name — the
    // sentence still names the server it is about.
    return '';
  }
}

/** The one fallback sentence, as a value a surface can paint. */
export const localRouteFallback = routeFallback;

/**
 * The local route for this window stopped answering: hand the session to the
 * tunnel and say so once.
 *
 * Called by the connection watcher (`composables/useConnectionLost.ts`) when a
 * live session drops. Answers `true` when the app is back on the cloud route, so
 * the caller does not paint a connection-lost overlay for a session it just
 * saved. Returns `false` for anything else — no cloud session, a window nobody
 * identified, a window that is not on a LAN route at all, a tunnel that cannot
 * be established — and the normal lost-session handling takes over.
 *
 * Nothing is read from storage but the window's own record: there is no stored
 * transport preference any more, and being ON the LAN route is itself the fact
 * that the route was resolved locally (`connectResolvingRoute`).
 */
export async function fallbackToCloudRoute(): Promise<boolean> {
  const auth = useAuthStore();
  if (!auth.isCloudAuthenticated) return false;

  // The tab knows the hub it is on through sessionStorage (`readTabActiveHub`),
  // which carries the id and the address — the only place the fallback can learn
  // what to hand back to the tunnel.
  const tab = readTabActiveHub();
  const endpoint = tab ?? getActiveHubEndpoint();
  if (!endpoint) return false;

  // The tunnel needs the hub's **cloud row** id, which is what a launcher-driven
  // connect passes as `hubId` (`ServersSection.vue` → `openServerTab`); the
  // window's own key carries the hub's own id, the second spelling. A hub the
  // account cannot name under either is refused by `connectCloudSilently` below,
  // which is the honest answer: nothing here invents a route.
  const hubId = (tab?.hubId || getActiveHubOwnId() || '').trim();
  if (!hubId) return false;

  // Only a window that is actually ON the local route falls back: a session that
  // is already on the tunnel has nothing to hand over, and neither has a window
  // whose API base is not this hub's own LAN address (a LAN session dials
  // `otto-local://<ip>:<port>/api`; a tunnelled one dials the cloud's API URL).
  const webrtc = useWebRTCStore();
  if ((webrtc.connected || webrtc.connecting) && webrtc.hubId === hubId) return false;
  const active = parseHubApiUrl(getRuntimeConfig().apiUrl);
  if (!active || active.ip !== endpoint.ip || active.port !== endpoint.port) return false;

  const name = tab?.name || readActiveHubName() || hubId;
  const result = await connectCloudSilently({ mode: 'cloud', hubId, name });
  if (!result.ok) return false;

  if (!stateRouteFallback({ hubId, hubName: name, reason: 'lost' })) {
    // The sentence was already said for this hub this session — the session is
    // still saved, it is just not announced twice.
    console.info('[LocalRoute] back on the tunnel (already stated)', { hubId });
  }
  return true;
}

/** The surface acknowledged the notice. */
export function clearLocalRouteFallback(): void {
  clearRouteFallback();
}
